import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Button, Splitter } from "antd";
import * as React from "react";
import { classNames } from "src/common/classNames";

import * as styles from "./LeftCollapsedPanels.module.less";

type ReactNode = React.ReactNode;

type PropsLeftCollapsedPanels = {
  leftTitle: ReactNode | (() => ReactNode);
  leftPart: ReactNode | (() => ReactNode);
  children: ReactNode;
  className?: string;
  /** начальное состояние: левая панель свёрнута */
  leftClosed?: boolean;
};

export const LeftCollapsedPanels: React.FC<PropsLeftCollapsedPanels> = (
  props,
) => {
  const { leftTitle, leftPart, children, className, leftClosed } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [sizes, setSizes] = React.useState<number[]>();
  const initialLeftSize = leftClosed ? minSize : defaultSidebarSize;

  const sidebarSize = sizes?.[0] ?? initialLeftSize;
  const isContentVisible = contentVisible(sidebarSize);

  const setLeftSize = (newLeft: number) => {
    const left = sizes?.[0] ?? initialLeftSize;
    const right =
      sizes?.[1] ??
      Math.max(0, (containerRef.current?.clientWidth ?? 0) - left);
    setSizes([newLeft, right + left - newLeft]);
  };

  const onToggleSidebar = () => {
    setLeftSize(isContentVisible ? minSize : maxSize);
  };

  const onResize = (nextSizes: number[]) => {
    setSizes(nextSizes);
  };

  /**
   * если контент не виден, то устанавливаем минимальный размер,
   * чтобы обеспечить "прилипание" панели к левому краю, потому что
   * нет смысла показывать панель, если контент не виден
   */
  const onResizeEnd = () => {
    if (!contentVisible(sidebarSize)) {
      setLeftSize(minSize);
    }
  };

  const viewLeft = (node: ReactNode | (() => ReactNode)): ReactNode => {
    if (typeof node === "function") {
      return node();
    }
    return node;
  };

  return (
    <div
      ref={containerRef}
      className={classNames([styles.container, className])}
    >
      <Splitter
        className={styles.splitter}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
      >
        <Splitter.Panel
          size={sizes?.[0]}
          defaultSize={initialLeftSize}
          min={minSize}
          max={maxSize}
          className={classNames([
            styles.sidebar,
            [isContentVisible, styles.open],
          ])}
        >
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarHeaderTitle}>
              {viewLeft(leftTitle)}
            </span>
            <Button
              type="text"
              icon={
                isContentVisible ? (
                  <MenuFoldOutlined className={styles.icon} />
                ) : (
                  <MenuUnfoldOutlined className={styles.icon} />
                )
              }
              onClick={onToggleSidebar}
            />
          </div>

          <div className={styles.sidebarContent}>{viewLeft(leftPart)}</div>
        </Splitter.Panel>
        <Splitter.Panel size={sizes?.[1]}>{children}</Splitter.Panel>
      </Splitter>
    </div>
  );
};

const delta = 80;
const minSize = 56;
const maxSize = 368;
const defaultSidebarSize = 280;
const contentVisible = (size: number) => size > minSize + delta;
